import React, { useState, useEffect } from "react";
import { X, Plus, Info, ChevronDown, RefreshCw } from "lucide-react";

const RoomCreateModal = ({
  isOpen,
  onClose,
  onCreateRoom,
  initialData = null,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    roomName: "rpf-nxc-phay",
    enableShortLink: false,
    roomType: "Meeting",
    moderatorApproval: true,
    passwordRequired: false,
    presentersAllowed: true,
    moderatorCount: 1,
    participantCount: 10,
  });

  const [showModeratorDropdown, setShowModeratorDropdown] = useState(false);
  const [showRoomTypeDropdown, setShowRoomTypeDropdown] = useState(false);
  const [showModeratorCountDropdown, setShowModeratorCountDropdown] =
    useState(false);
  const [showParticipantCountDropdown, setShowParticipantCountDropdown] =
    useState(false);

  const roomTypes = ["Meeting", "Webinar"];
  const moderatorCounts = Array.from({ length: 3 }, (_, i) => i + 1);
  // 1 to 49
  const participantCounts = Array.from({ length: 49 }, (_, i) => i + 1);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleModeratorRemove = (moderator) => {
    setFormData((prev) => ({
      ...prev,
      moderators: prev.moderators.filter((m) => m !== moderator),
    }));
  };

  const handleModeratorAdd = (moderator) => {
    if (!formData.moderators.includes(moderator)) {
      setFormData((prev) => ({
        ...prev,
        moderators: [...prev.moderators, moderator],
      }));
    }
  };

  const generateRoomName = () => {
    const adjectives = [
      "Swift",
      "Bright",
      "Dynamic",
      "Creative",
      "Smart",
      "Quick",
      "Bold",
      "Sharp",
      "Bright",
      "Clear",
      "Fast",
      "Smart",
      "Cool",
      "Fresh",
      "New",
      "Prime",
      "Elite",
      "Pro",
      "Max",
      "Ultra",
      "Super",
      "Mega",
      "Turbo",
      "Rapid",
    ];

    const nouns = [
      "Meeting",
      "Room",
      "Space",
      "Hub",
      "Zone",
      "Area",
      "Place",
      "Spot",
      "Conference",
      "Session",
      "Gathering",
      "Workshop",
      "Studio",
      "Lab",
      "Center",
      "Base",
    ];

    const randomAdjective =
      adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const randomNumber = Math.floor(Math.random() * 9999) + 1;

    return `${randomAdjective}-${randomNoun}-${randomNumber}`;
  };

  const handleGenerateRoomName = () => {
    const newRoomName = generateRoomName();
    setFormData((prev) => ({
      ...prev,
      roomName: newRoomName,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreateRoom(formData);
  };

  const getRoomType = (roomType) => {
    return String(roomType).toLowerCase() == "conferencing"
      ? "Meeting"
      : roomType;
  };

  // Generate a room name when modal opens (only for create mode)
  useEffect(() => {
    if (isOpen) {
      if (isEdit && initialData) {
        // For edit mode, populate with existing data
        setFormData({
          roomName: initialData.name || "",
          enableShortLink: initialData.enableShortLink || false,
          roomType: getRoomType(initialData.roomType) || "Meeting",
          moderatorApproval: initialData.moderatorApproval || true,
          passwordRequired: initialData.passwordRequired || false,
          presentersAllowed: initialData.presentersAllowed || true,
          moderatorCount: initialData.moderatorCount || 1,
          participantCount: initialData.participantCount || 10,
        });
      } else {
        // For create mode, generate a new room name
        const newRoomName = generateRoomName();
        setFormData((prev) => ({
          ...prev,
          roomName: newRoomName,
        }));
      }
    }
  }, [isOpen, isEdit, initialData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0000] bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[95vh] overflow-y-auto border border-gray-700 transform transition-all duration-300 ease-out">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">
              {isEdit ? "Edit Room" : "Create New Room"}
            </h2>
            <p className="text-gray-400 text-xs">
              {isEdit
                ? "Update your meeting room settings"
                : "Configure your meeting room settings"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700/50 rounded-full transition-all duration-200 hover:scale-110 group"
          >
            <X className="h-4 w-4 text-gray-300 group-hover:text-white transition-colors" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Room Name */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-white mb-2 flex items-center">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
              Room Name *
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.roomName}
                onChange={(e) => handleInputChange("roomName", e.target.value)}
                className="w-full px-3 py-3 pr-20 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 backdrop-blur-sm text-sm"
                placeholder="Enter room name..."
                required
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
                <button
                  type="button"
                  onClick={handleGenerateRoomName}
                  className="p-1.5 hover:bg-gray-700/50 rounded-md transition-all duration-200 hover:scale-110 group"
                  title="Generate random room name"
                >
                  <RefreshCw className="h-3 w-3 text-gray-400 group-hover:text-purple-400 transition-colors" />
                </button>
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            <p className="text-xs text-gray-400 bg-gray-800/30 px-2 py-1.5 rounded-md border border-gray-700">
              💡 Room name should not be left empty! If left empty, it will save
              a random name, which can be edited later.
            </p>
          </div>

          {/* Enable Short Room Link */}
          <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all duration-200">
            <div className="flex items-center">
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-2"></div>
              <label className="text-xs font-semibold text-white">
                Enable short room link
              </label>
              <Info className="h-3 w-3 text-gray-400 ml-2 hover:text-purple-400 transition-colors cursor-help" />
            </div>
            <button
              type="button"
              onClick={() =>
                handleInputChange("enableShortLink", !formData.enableShortLink)
              }
              className={`relative inline-flex h-6 w-10 items-center rounded-full transition-all duration-300 ${
                formData.enableShortLink
                  ? "bg-gradient-to-r from-purple-600 to-purple-700 shadow-lg shadow-purple-500/25"
                  : "bg-gray-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 shadow-lg ${
                  formData.enableShortLink ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Room Type */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-white mb-2 flex items-center">
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2"></div>
              Room type *
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowRoomTypeDropdown(!showRoomTypeDropdown)}
                className="w-full px-3 py-3 border border-gray-600 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 flex items-center justify-between bg-gray-800/50 text-white hover:bg-gray-700/50 transition-all duration-200 backdrop-blur-sm text-sm"
              >
                <span className="font-medium">{formData.roomType}</span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                    showRoomTypeDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showRoomTypeDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl z-20 backdrop-blur-sm">
                  {roomTypes.map((type, index) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        handleInputChange("roomType", type);
                        setShowRoomTypeDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200 flex items-center ${
                        index === 0 ? "rounded-t-lg" : ""
                      } ${
                        index === roomTypes.length - 1 ? "rounded-b-lg" : ""
                      }`}
                    >
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2"></div>
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Access Permission Toggles */}
          {/* <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center">
              <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mr-2"></div>
              Access Permissions
            </h3>

            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all duration-200">
                <div className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></div>
                  <label className="text-xs font-semibold text-white">
                    Is moderator approval required to Join ?
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    handleInputChange(
                      "moderatorApproval",
                      !formData.moderatorApproval
                    )
                  }
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-all duration-300 ${
                    formData.moderatorApproval
                      ? "bg-gradient-to-r from-purple-600 to-purple-700 shadow-lg shadow-purple-500/25"
                      : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 shadow-lg ${
                      formData.moderatorApproval
                        ? "translate-x-5"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all duration-200">
                <div className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-2"></div>
                  <label className="text-xs font-semibold text-white">
                    Password needed to join ?
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    handleInputChange(
                      "passwordRequired",
                      !formData.passwordRequired
                    )
                  }
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-all duration-300 ${
                    formData.passwordRequired
                      ? "bg-gradient-to-r from-purple-600 to-purple-700 shadow-lg shadow-purple-500/25"
                      : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 shadow-lg ${
                      formData.passwordRequired
                        ? "translate-x-5"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all duration-200">
                <div className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                  <label className="text-xs font-semibold text-white">
                    Are presenters allowed to join?
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    handleInputChange(
                      "presentersAllowed",
                      !formData.presentersAllowed
                    )
                  }
                  className={`relative inline-flex h-6 w-10 items-center rounded-full transition-all duration-300 ${
                    formData.presentersAllowed
                      ? "bg-gradient-to-r from-purple-600 to-purple-700 shadow-lg shadow-purple-500/25"
                      : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 shadow-lg ${
                      formData.presentersAllowed
                        ? "translate-x-5"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div> */}

          {/* Participant Count Inputs */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-white mb-2 flex items-center">
                  <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full mr-2"></div>
                  No. Of Moderators
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setShowModeratorCountDropdown(!showModeratorCountDropdown)
                    }
                    className="w-full px-3 py-3 border border-gray-600 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 flex items-center justify-between bg-gray-800/50 text-white hover:bg-gray-700/50 transition-all duration-200 backdrop-blur-sm text-sm"
                  >
                    <span className="font-medium">
                      {formData.moderatorCount}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                        showModeratorCountDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showModeratorCountDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl z-20 backdrop-blur-sm">
                      {moderatorCounts.map((count, index) => (
                        <button
                          key={count}
                          type="button"
                          onClick={() => {
                            handleInputChange("moderatorCount", count);
                            setShowModeratorCountDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200 flex items-center ${
                            index === 0 ? "rounded-t-lg" : ""
                          } ${
                            index === moderatorCounts.length - 1
                              ? "rounded-b-lg"
                              : ""
                          }`}
                        >
                          <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full mr-2"></div>
                          {count}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-semibold text-white mb-2 flex items-center">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  No. Of Participants
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setShowParticipantCountDropdown(
                        !showParticipantCountDropdown
                      )
                    }
                    className="w-full px-3 py-3 border border-gray-600 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 flex items-center justify-between bg-gray-800/50 text-white hover:bg-gray-700/50 transition-all duration-200 backdrop-blur-sm text-sm"
                  >
                    <span className="font-medium">
                      {formData.participantCount}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                        showParticipantCountDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showParticipantCountDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl z-20 backdrop-blur-sm max-h-48 overflow-y-auto">
                      {participantCounts.map((count, index) => (
                        <button
                          key={count}
                          type="button"
                          onClick={() => {
                            handleInputChange("participantCount", count);
                            setShowParticipantCountDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200 flex items-center ${
                            index === 0 ? "rounded-t-lg" : ""
                          } ${
                            index === participantCounts.length - 1
                              ? "rounded-b-lg"
                              : ""
                          }`}
                        >
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                          {count}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700 bg-gradient-to-r from-gray-800/20 to-gray-900/20 -mx-6 -mb-6 px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-700/50 border border-gray-600 rounded-lg hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 hover:scale-105 backdrop-blur-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 text-xs font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 border border-transparent rounded-lg hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-lg shadow-purple-500/25 transition-all duration-200 hover:scale-105 hover:shadow-purple-500/40"
            >
              {isEdit ? "✨ Update Room" : "✨ Create Room"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoomCreateModal;
